import { SubsystemSelectDto } from '@@types/api.types';
import { IconButton } from '@components/core/icon-button.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { ExpandableText } from '@components/shared/expandable-text.component';
import { formatProper, formatUsd, mapCountryCodeToCountry } from '@utils/text.util';
import { ChevronDown, ChevronUp, EditIcon } from 'lucide-react';
import { FunctionComponent, useState } from 'react';
import { TooltipComponent } from '../../../core/tooltip.component';

type SubsystemItemProps = {
  subsystem: SubsystemSelectDto;
  onEdit?: () => void;
};

export const SubsystemItem: FunctionComponent<SubsystemItemProps> = (props) => {
  const {
    subsystem: { category, components, description, name, origin, subcategory, type },
    onEdit,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  return (
    <div
      className="flex flex-col gap-2 h-fit rounded-md p-2 bg-gray-100"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="bg-white rounded-md flex flex-col gap-2 p-2">
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-0">
            <div className="text-gray-600 font-medium text-[16px]">{name}</div>
            <BlueLabel text={`${formatProper(type)} Subsystem`} />
          </div>
          {onEdit && (
            <IconButton
              ariaLabel="Update Subsystem"
              icon={<EditIcon />}
              isVisible={isHovering}
              onClick={onEdit}
              variant="secondary"
            />
          )}
        </div>

        <div
          className="flex justify-between items-center cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="text-xs font-normal text-[#1F1C1AB2]">
            {components.length} Component{components.length === 1 ? '' : 's'}
          </div>
          {isOpen ? (
            <ChevronUp
              size={20}
              className="text-[#1F1C1AB2]"
            />
          ) : (
            <ChevronDown
              size={20}
              className="text-[#1F1C1AB2]"
            />
          )}
        </div>
      </div>

      {isOpen && (
        <div className="flex flex-col gap-2 font-light text-xs text-gray-500 ">
          <div className="flex flex-col gap-2 p-2">
            <div className="flex items-center gap-1">
              <div>Category: {category.name}</div>
              {category.description && (
                <TooltipComponent
                  side="right"
                  className="text-xs whitespace-pre-wrap font-normal"
                  triggerClassName="text-gray-500 bg-white"
                >
                  {category.description}
                </TooltipComponent>
              )}
            </div>
            <div>Subcategory: {subcategory.name}</div>
            <div>Origin: {origin ? (mapCountryCodeToCountry(origin) ?? origin) : 'N/A'}</div>
            {description && (
              <ExpandableText
                className="font-light"
                maxLines={2}
                text={description}
              />
            )}
          </div>

          {components.map((component, index) => {
            const componentKey = component.id ?? `${component.name}-${index}`;
            return (
              <div
                key={componentKey}
                className="flex flex-col gap-2"
              >
                <div className="h-px border-t border-gray-300" />
                <div className="flex flex-col gap-2 p-2">
                  <div className="flex flex-col gap-0">
                    <div className="text-gray-600 font-medium text-sm">{component.name}</div>
                    <BlueLabel
                      className="text-xs"
                      text="Component"
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <div>
                      Origin:{' '}
                      {component.origin ? (mapCountryCodeToCountry(component.origin) ?? component.origin) : 'N/A'}
                    </div>
                    <div>Price per: {component.price ? formatUsd(component.price, 2) : 'N/A'}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
